import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { format, parseISO, differenceInMinutes } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { 
  MapPin, 
  ChevronDown,
  ChevronUp,
  Plane,
  Building,
  Map,
  Calendar,
  Clock,
  PlaneTakeoff,
  Utensils,
  Car,
  ArrowRight,
  Info,
  LogIn,
  LogOut,
  Bed
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { 
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import { formatSafeDate, formatCoverDateRange } from "@/lib/date-utils";
import { mapQuotationDatesFromDb, resolveCoverDates } from "@/lib/quotation-display-dates";
import { tripService } from '@/lib/supabase/trips';
import { 
  Quotation, 
  QuotationBlock, 
  QuotationPage, 
  FlightInfo as BaseFlightInfo,
  AccommodationInfo,
  TourInfo,
  TransportationInfo,
  RestaurantInfo,
  FlightEvent,
  AccommodationEvent
} from "@/components/quotation/types";
import { supabase } from '@/lib/supabase/client';
import { quotationService } from '@/lib/supabase/quotations';
import { getUnifiedItems } from '@/pages/client/QuotationView';

// Interface estendida para adicionar propriedades adicionais
interface FlightInfo extends BaseFlightInfo {
  title?: string;
  route?: string;
}

export default function QuotationPrintView() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [quotation, setQuotation] = useState<Quotation | null>(null);
  const [linkedTrip, setLinkedTrip] = useState<{ start_date?: string | null; end_date?: string | null } | null>(null);
  const [loading, setLoading] = useState(true);

  const coverDateRange = useMemo(() => {
    if (!quotation) return '';
    const { startDate, endDate } = resolveCoverDates(quotation, linkedTrip);
    return formatCoverDateRange(startDate, endDate);
  }, [quotation, linkedTrip]);
  const [expandedItemId, setExpandedItemId] = useState<string | null>(null);

  // Função para expandir/colapsar um item
  const toggleItem = (itemId: string) => {
    setExpandedItemId(expandedItemId === itemId ? null : itemId);
  };

  // Extrair todos os voos de todas as páginas
  const getAllFlights = useMemo(() => {
    if (!quotation || !quotation.pages) return [];
    
    return quotation.pages.reduce((allFlights, page) => {
      const pageFlights = (page.flights || []).map(flight => ({
        ...flight,
        _page: page.title || `Página ${page.id.substring(0, 8)}`
      }));
      return [...allFlights, ...pageFlights];
    }, [] as (FlightInfo & { _page?: string })[]);
  }, [quotation]);

  // Extrair todas as acomodações de todas as páginas
  const getAllAccommodations = useMemo(() => {
    if (!quotation || !quotation.pages) return [];
    
    return quotation.pages.reduce((allAccommodations, page) => {
      const pageAccommodations = (page.accommodations || []).map(accommodation => ({
        ...accommodation,
        _page: page.title || `Página ${page.id.substring(0, 8)}`
      }));
      return [...allAccommodations, ...pageAccommodations];
    }, [] as (AccommodationInfo & { _page?: string })[]);
  }, [quotation]);

  // Função para extrair blocos e tours de uma página específica
  const extractPageData = (page: QuotationPage): { 
    blocks: QuotationBlock[], 
    tours: TourInfo[], 
    transportations: TransportationInfo[], 
    restaurants: RestaurantInfo[],
    accommodations: AccommodationInfo[],
    flights: FlightInfo[],
    flightEvents: FlightEvent[],
    accommodationEvents: AccommodationEvent[]
  } => {
    return {
      blocks: page.blocks || [],
      tours: page.tours || [],
      transportations: page.transportations || [],
      restaurants: page.restaurants || [],
      accommodations: page.accommodations || [],
      flights: page.flights || [],
      flightEvents: page.flightEvents || [],
      accommodationEvents: page.accommodationEvents || []
    };
  };

  // Carregar cotação
  useEffect(() => {
    const loadQuotation = async () => {
      try {
        if (id && id !== 'preview') {
          try {
            const quotationData = await quotationService.getQuotationById(id);
            
            if (quotationData) {
              console.log('Carregando cotação do Supabase para impressão:', quotationData);
              
              const processedQuotation: any = {
                id: quotationData.id,
                title: quotationData.title || '',
                client: quotationData.client || '',
                destination: quotationData.destination || [],
                ...mapQuotationDatesFromDb(quotationData.start_date, quotationData.end_date),
                status: 'draft',
                notes: quotationData.notes || '',
                pages: quotationData.pages || [],
                coverImages: quotationData.cover_images || [],
                createdAt: quotationData.created_at ? new Date(quotationData.created_at) : new Date(),
                updatedAt: quotationData.updated_at ? new Date(quotationData.updated_at) : new Date(),
                tripId: quotationData.trip_id,
                showRecommendationsTab: quotationData.show_recommendations_tab !== undefined 
                  ? quotationData.show_recommendations_tab 
                  : true,
                showPriceTab: quotationData.show_price_tab !== undefined 
                  ? quotationData.show_price_tab 
                  : true,
                recommendationsTabName: quotationData.recommendations_tab_name,
                recommendationGroups: quotationData.recommendation_groups || [],
                recommendations: [],
                useDayMode: quotationData.use_day_mode,
                usePageDropdown: quotationData.use_page_dropdown,
                showMapLines: quotationData.show_map_lines,
                showAccommodationsInItinerary: quotationData.show_accommodations_in_itinerary,
                accommodationGroups: quotationData.accommodation_groups || [],
                useCustomAccommodationGroups: quotationData.use_custom_accommodation_groups || false,
                accommodationsTabName: quotationData.accommodations_tab_name,
                showAccommodationsTab: quotationData.show_accommodations_tab !== false,
                accommodationsDescription: quotationData.accommodations_description || '',
                showFlightsTab: quotationData.show_flights_tab !== false,
                flightsTabName: quotationData.flights_tab_name,
                showFlightsInItinerary: quotationData.show_flights_in_itinerary !== false,
                flightGroups: quotationData.flight_groups || [],
                useCustomFlightGroups: quotationData.use_custom_flight_groups || false,
                flightsDescription: quotationData.flights_description || '',
                priceTabName: quotationData.price_tab_name,
                priceDetails: quotationData.price_details || '',
                showCustomTab: quotationData.show_custom_tab,
                customTabName: quotationData.custom_tab_name,
                customTabGroups: quotationData.custom_tab_groups || [],
              };
              
              setQuotation(processedQuotation);
              if (processedQuotation.tripId) {
                const tripData = await tripService.getTripById(processedQuotation.tripId);
                setLinkedTrip(tripData);
              } else {
                setLinkedTrip(null);
              }
              setLoading(false);
              return;
            }
          } catch (error) {
            console.error('Erro ao carregar cotação do Supabase:', error);
          }
        }
        
        setLoading(false);
      } catch (error) {
        console.error('Error in loadQuotation:', error);
        setLoading(false);
      }
    };

    loadQuotation();
  }, [id]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-white">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-voah-teal mx-auto"></div>
          <p className="mt-4 text-voah-teal">Carregando para impressão...</p>
        </div>
      </div>
    );
  }

  if (!quotation) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-white">
        <div className="text-center p-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Cotação não encontrada</h2>
          <p className="mb-6 text-gray-600">A cotação que você está procurando não está disponível.</p>
          <Button 
            onClick={() => navigate(-1)}
            className="bg-voah-teal hover:bg-voah-teal-dark"
          >
            Voltar
          </Button>
        </div>
      </div>
    );
  }

  // Format destination for display
  const destinationDisplay = typeof quotation.destination === 'string' 
    ? quotation.destination 
    : quotation.destination.join(', ');
    
  // Function to get client names either from the string or from the linked trip
  const formatClientNames = (clientsString: string | undefined) => {
    if (quotation.tripId) {
      try {
        const savedTrips = localStorage.getItem('voah_trips');
        if (savedTrips) {
          const trips = JSON.parse(savedTrips);
          const linkedTrip = trips.find((trip: any) => trip.id === quotation.tripId);
          
          if (linkedTrip && linkedTrip.clientIds && linkedTrip.clientIds.length > 0) {
            const savedClients = localStorage.getItem('voah_clients');
            if (savedClients) {
              const clients = JSON.parse(savedClients);
              const tripClients = clients.filter((client: any) => 
                linkedTrip.clientIds.includes(client.id)
              );
              
              return tripClients
                .map((client: any) => client.name)
                .join(', ');
            }
          }
        }
      } catch (error) {
        // Error silenciado
      }
    }
    
    return clientsString || '';
  };

  // Função utilitária para formatar datas yyyy-mm-dd para dd/mm/yyyy
  function formatDateBR(dateStr?: string) {
    if (!dateStr) return '';
    const [date] = dateStr.split('T');
    const [yyyy, mm, dd] = date.split('-');
    if (yyyy && mm && dd) return `${dd}/${mm}/${yyyy}`;
    return dateStr;
  }

  return (
    <div id="quotation-print-content" className="min-h-screen bg-white print:bg-white">
      {/* Estilos específicos para impressão */}
      <style>{`
        @media print {
          .print-break {
            page-break-before: always;
          }
          .print-avoid-break {
            page-break-inside: avoid;
          }
          @page {
            margin: 1.5cm 1cm 1.5cm 1cm !important;
            size: A4;
          }
          body {
            margin: 0 !important;
            padding: 0 !important;
          }
          #quotation-print-content {
            margin: 0 !important;
            padding: 0 !important;
          }
        }
      `}</style>

      {/* Header com logo e título */}
      <div className="bg-white border-b-2 border-voah-teal mb-6 print:hidden">
        <div className="max-w-4xl mx-auto px-6">
          <div className="relative flex items-center py-4">
            {/* Logo à esquerda */}
            <div className="flex items-center">
              <img 
                src="/lovable-uploads/3aedb010-4564-4186-8687-ad2d8f6d7160.png" 
                alt="VOAH Logo" 
                className="h-12 w-auto"
              />
            </div>
            
            {/* Título centralizado absolutamente */}
            <div className="absolute left-1/2 transform -translate-x-1/2 flex flex-col items-center text-center">
              <h1 className="font-bold text-voah-teal leading-tight" style={{ fontSize: 'clamp(1.25rem, 3.2vw, 2.25rem)', maxWidth: '60vw', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{quotation.title}</h1>
              <p className="text-gray-600">{formatClientNames(quotation.client)}</p>
            </div>
            
            {/* Informações à direita */}
            <div className="ml-auto text-right">
              <div className="flex items-center text-gray-600 mb-1">
                <MapPin className="w-4 h-4 mr-1" />
                <span>{destinationDisplay}</span>
              </div>
              {coverDateRange && (
                <div className="flex items-center text-gray-500 text-sm">
                  <Calendar className="w-3 h-3 mr-1" />
                  <span>{coverDateRange}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Cabeçalho somente na primeira página de impressão */}
      <div className="hidden print:block print:mb-8">
        <div className="max-w-4xl mx-auto px-6">
          <div className="flex items-center py-2 border-b-2 border-voah-teal">
            <img src="/lovable-uploads/3aedb010-4564-4186-8687-ad2d8f6d7160.png" alt="VOAH Logo" className="h-10 w-auto mr-4" />
            <div className="flex-1 min-w-0">
              <div className="font-bold text-voah-teal leading-tight" style={{ fontSize: 'clamp(1.25rem, 3.2vw, 2.25rem)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{quotation.title}</div>
              <div className="text-sm text-gray-600 truncate">{formatClientNames(quotation.client)}</div>
            </div>
            <div className="text-right text-sm text-gray-600">
              <div className="flex items-center justify-end">
                <MapPin className="w-4 h-4 mr-1" />
                <span className="truncate max-w-[30vw]">{destinationDisplay}</span>
              </div>
              {coverDateRange && (
                <div className="flex items-center justify-end text-gray-500">
                  <Calendar className="w-3 h-3 mr-1" />
                  <span>{coverDateRange}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Conteúdo principal */}
      <div className="max-w-4xl mx-auto px-6">
        {/* Página Boas Vindas */}
        <div className="print-break">
          <div className="mb-6">
            <h2 className="text-3xl font-bold text-voah-teal mb-2">Boas Vindas</h2>
          </div>
          
          {/* Conteúdo da página Boas Vindas */}
          {!quotation.notes ? (
            <div className="mb-6">
              <div className="bg-gray-100 p-4 rounded-md text-center text-gray-500">
                Sem informações adicionais disponíveis.
              </div>
            </div>
          ) : (
            <div className="mb-6">
              <div 
                className="prose prose-sm max-w-none prose-headings:text-voah-teal prose-headings:font-medium prose-p:text-gray-700 prose-strong:text-gray-800 prose-strong:font-medium prose-a:text-voah-teal prose-a:no-underline hover:prose-a:underline prose-li:text-gray-700 prose-li:marker:text-voah-teal text-left text-[14px] leading-loose magazine-content"
                dangerouslySetInnerHTML={{ 
                  __html: quotation.notes
                    .replace(/style="[^"]*"/g, '')
                    .replace(/style='[^']*'/g, '')
                    .replace(/<p>/g, '<p class="mb-4 px-2 py-1 drop-cap-paragraph">')
                    .replace(/<p class="mb-4 px-2 py-1 drop-cap-paragraph">(\s*)<\/p>/g, '')
                }} 
              />
            </div>
          )}
        </div>

        {/* Renderizar todas as páginas em sequência */}
        {quotation.pages.filter(page => page.isVisible !== false).map((page, index) => (
          <div key={`page-${page.id}`} className={index > 0 ? 'print-break' : ''}>
            {/* Título da página */}
            <div className="mb-6">
              <h2 className="text-3xl font-bold text-voah-teal mb-2">{page.title}</h2>
              {(page.startDate || page.endDate) && (
                <div className="flex items-center text-sm text-gray-500 mb-4">
                  <Calendar className="mr-2 h-4 w-4" />
                  <span>
                    {formatSafeDate(page.startDate, "dd/MM/yyyy")} 
                    {page.endDate && page.startDate && page.endDate !== page.startDate && (
                      <>
                        {" - "}
                        {formatSafeDate(page.endDate, "dd/MM/yyyy")}
                      </>
                    )}
                  </span>
                </div>
              )}
            </div>

            {/* Descrição da página */}
            {page.description && page.description.replace(/<[^>]*>/g, '').trim() !== '' && (
              <div className="mb-6">
                <div 
                  className="text-gray-700 leading-relaxed prose prose-sm max-w-none text-left"
                  dangerouslySetInnerHTML={{ 
                    __html: page.description
                      .replace(/style=\"[^\"]*\"/g, '')
                      .replace(/style='[^']*'/g, '')
                  }}
                />
              </div>
            )}

            {/* Conteúdo da página */}
            <PageContent 
              page={page}
              quotation={quotation}
              getAllFlights={getAllFlights}
              getAllAccommodations={getAllAccommodations}
              extractPageData={extractPageData}
              getUnifiedItems={getUnifiedItems}
              expandedItemId={expandedItemId}
              toggleItem={toggleItem}
              formatDateBR={formatDateBR}
            />
          </div>
        ))}

        {/* Página de Hospedagens */}
        {quotation.showAccommodationsTab !== false && (
          <div className="print-break">
            <div className="mb-6">
              <h2 className="text-3xl font-bold text-voah-teal mb-2">
                {quotation.accommodationsTabName || 'Hospedagens'}
              </h2>
            </div>
            
            {/* Descrição das hospedagens */}
            {quotation.accommodationsDescription && (
              <div className="mb-6">
                <div 
                  className="text-gray-700 leading-relaxed prose prose-sm max-w-none text-left"
                  dangerouslySetInnerHTML={{ 
                    __html: quotation.accommodationsDescription
                      .replace(/style=\"[^\"]*\"/g, '')
                      .replace(/style='[^']*'/g, '')
                  }}
                />
              </div>
            )}

            {/* Lista de hospedagens */}
            <div className="space-y-4 mb-8">
              {getAllAccommodations.map((accommodation, index) => (
                <div key={accommodation.id} className="mb-4 border border-gray-200 rounded-lg overflow-hidden shadow-sm print-avoid-break">
                  {/* Título da hospedagem */}
                  <div className="px-4 py-3 bg-gray-50 border-b">
                    <h3 className="text-lg font-bold text-voah-teal flex items-center">
                      <span className="text-voah-teal mr-2 font-bold">
                        {index + 1}.
                      </span>
                      {accommodation.name}
                    </h3>
                  </div>
                  
                  {/* Conteúdo da hospedagem */}
                  <div className="p-4">
                    {/* Informações da hospedagem */}
                    <div className="mb-3 bg-gray-50 p-3 rounded border">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {/* Localização */}
                        {accommodation.location && (
                          <div className="flex items-center">
                            <div className="mr-2 text-voah-teal">
                              <MapPin className="h-4 w-4" />
                            </div>
                            <div>
                              <span className="text-xs text-gray-500">Localização</span>
                              <div className="text-sm font-medium">{accommodation.location}</div>
                            </div>
                          </div>
                        )}
                        
                        {/* Check-in e Check-out */}
                        {accommodation.checkInTime && (
                          <div className="flex items-center">
                            <div className="mr-2 text-green-600">
                              <LogIn className="h-4 w-4" />
                            </div>
                            <div>
                              <span className="text-xs text-gray-500">Check-in</span>
                              <div className="text-sm font-medium">
                                {accommodation.checkInDate && `${accommodation.checkInDate} `}
                                {accommodation.checkInTime && `${accommodation.checkInTime}`}
                              </div>
                            </div>
                          </div>
                        )}
                        
                        {accommodation.checkOutTime && (
                          <div className="flex items-center">
                            <div className="mr-2 text-red-600">
                              <LogOut className="h-4 w-4" />
                            </div>
                            <div>
                              <span className="text-xs text-gray-500">Check-out</span>
                              <div className="text-sm font-medium">
                                {accommodation.checkOutDate && `${accommodation.checkOutDate} `}
                                {accommodation.checkOutTime && `${accommodation.checkOutTime}`}
                              </div>
                            </div>
                          </div>
                        )}
                        
                        {/* Tipo de quarto */}
                        {accommodation.roomType && (
                          <div className="flex items-center">
                            <div className="mr-2 text-voah-teal">
                              <Bed className="h-4 w-4" />
                            </div>
                            <div>
                              <span className="text-xs text-gray-500">Tipo de Quarto</span>
                              <div className="text-sm font-medium">{accommodation.roomType}</div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {/* Descrição da hospedagem */}
                    {accommodation.description && (
                      <div 
                        className="text-sm text-gray-700 leading-relaxed prose prose-sm max-w-none text-left"
                        dangerouslySetInnerHTML={{ 
                          __html: accommodation.description
                            .replace(/style=\"[^\"]*\"/g, '')
                            .replace(/style='[^']*'/g, '')
                        }}
                      />
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Página de Voos */}
        {quotation.showFlightsTab !== false && (
          <div className="print-break">
            <div className="mb-6">
              <h2 className="text-3xl font-bold text-voah-teal mb-2">
                {quotation.flightsTabName || 'Voos'}
              </h2>
            </div>
            
            {/* Descrição dos voos */}
            {quotation.flightsDescription && (
              <div className="mb-6">
                <div 
                  className="text-gray-700 leading-relaxed prose prose-sm max-w-none text-left"
                  dangerouslySetInnerHTML={{ 
                    __html: quotation.flightsDescription
                      .replace(/style=\"[^\"]*\"/g, '')
                      .replace(/style='[^']*'/g, '')
                  }}
                />
              </div>
            )}

            {/* Lista de voos */}
            <div className="space-y-4 mb-8">
              {getAllFlights.map((flight, index) => (
                <div key={flight.id} className="mb-4 border border-gray-200 rounded-lg overflow-hidden shadow-sm print-avoid-break">
                  {/* Título do voo */}
                  <div className="px-4 py-3 bg-blue-50 text-blue-700 border-b">
                    <h3 className="text-lg font-bold flex items-center">
                      <span className="text-blue-700 mr-2 font-bold">
                        {index + 1}.
                      </span>
                      {flight.airline || 'Companhia aérea'} {flight.flightNumber && `• ${flight.flightNumber}`}
                    </h3>
                  </div>
                  
                  {/* Conteúdo do voo */}
                  <div className="p-4">
                    <div className="bg-white rounded border">
                      {/* Header do voo */}
                      <div className="bg-voah-teal text-white p-3">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center">
                            <PlaneTakeoff className="h-5 w-5 mr-2" />
                            <h4 className="text-lg font-semibold">
                              {flight.flightNumber ? `Voo ${flight.airline} ${flight.flightNumber}` : (flight.airline || 'Voo')}
                            </h4>
                          </div>
                          {flight.price && (
                            <div className="text-lg font-medium">
                              {flight.price.toLocaleString('pt-BR', { 
                                style: 'currency', 
                                currency: flight.currency || 'BRL' 
                              })}
                            </div>
                          )}
                        </div>
                      </div>
                      
                      {/* Flight route visualization simplificada */}
                      <div className="p-4">
                        <div className="flex items-center justify-between">
                          {/* Departure information */}
                          <div className="text-center flex-1">
                            <div className="text-sm text-gray-500 mb-1">Partida</div>
                            <div className="text-xl font-bold text-voah-teal">
                              {flight.departureAirport}
                            </div>
                            {flight.departureDateTime && (
                              <div className="text-sm text-gray-700">
                                {formatSafeDate(flight.departureDateTime, "dd/MM/yyyy")} • {flight.departureDateTime.split('T')[1]?.substring(0, 5)}
                              </div>
                            )}
                          </div>
                          
                          {/* Arrow */}
                          <div className="flex items-center justify-center px-4">
                            <ArrowRight className="text-voah-teal h-6 w-6" />
                          </div>
                          
                          {/* Arrival information */}
                          <div className="text-center flex-1">
                            <div className="text-sm text-gray-500 mb-1">Chegada</div>
                            <div className="text-xl font-bold text-voah-teal">
                              {flight.arrivalAirport}
                            </div>
                            {flight.arrivalDateTime && (
                              <div className="text-sm text-gray-700">
                                {formatSafeDate(flight.arrivalDateTime, "dd/MM/yyyy")} • {flight.arrivalDateTime.split('T')[1]?.substring(0, 5)}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Página de Preços */}
        {quotation.showPriceTab && (
          <div className="print-break">
            <div className="mb-6">
              <h2 className="text-3xl font-bold text-voah-teal mb-2">
                {quotation.priceTabName || 'Preços'}
              </h2>
            </div>
            
            {/* Conteúdo dos preços */}
            {!quotation.priceDetails ? (
              <div className="mb-6">
                <div className="bg-gray-100 p-4 rounded-md text-center text-gray-500">
                  Sem informações de preço disponíveis.
                </div>
              </div>
            ) : (
              <div className="mb-6">
                <div 
                  className="prose prose-sm max-w-none prose-headings:text-voah-teal prose-headings:font-medium prose-p:text-gray-700 prose-strong:text-gray-800 prose-strong:font-medium prose-a:text-voah-teal prose-a:no-underline hover:prose-a:underline prose-li:text-gray-700 prose-li:marker:text-voah-teal text-left text-[14px] leading-loose"
                  dangerouslySetInnerHTML={{ 
                    __html: quotation.priceDetails
                      .replace(/style=\"[^\"]*\"/g, '')
                      .replace(/style='[^']*'/g, '')
                      .replace(/<p>/g, '<p class="mb-4 px-2 py-1">')
                      .replace(/<p class="mb-4 px-2 py-1">(\s*)<\/p>/g, '')
                  }} 
                />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// Componente para renderizar o conteúdo de uma página
function PageContent({ 
  page, 
  quotation, 
  getAllFlights, 
  getAllAccommodations, 
  extractPageData, 
  getUnifiedItems, 
  expandedItemId, 
  toggleItem, 
  formatDateBR 
}: {
  page: QuotationPage;
  quotation: Quotation;
  getAllFlights: any[];
  getAllAccommodations: any[];
  extractPageData: (page: QuotationPage) => any;
  getUnifiedItems: any;
  expandedItemId: string | null;
  toggleItem: (id: string) => void;
  formatDateBR: (dateStr?: string) => string;
}) {
  return (
    <div className="space-y-4 mb-8">
      {getUnifiedItems({
        blocks: extractPageData(page).blocks,
        tours: extractPageData(page).tours,
        transportations: extractPageData(page).transportations,
        restaurants: extractPageData(page).restaurants,
        accommodations: getAllAccommodations,
        flights: getAllFlights,
        flightEvents: extractPageData(page).flightEvents,
        accommodationEvents: extractPageData(page).accommodationEvents,
        showAccommodations: !!quotation.showAccommodationsInItinerary
      }).map((item: any, index: number) => {
        // Expandir todos os itens por padrão na impressão
        const isExpanded = true;

        // Determine item type and render appropriate card
        if (item.type === 'block') {
          return (
            <div key={item.id} className="mb-4 border border-gray-200 rounded-lg overflow-hidden shadow-sm print-avoid-break">
              {/* Título do bloco */}
              <div className="px-4 py-3 bg-gray-50 border-b">
                <h3 className="text-lg font-bold text-voah-teal flex items-center">
                  <span className="text-voah-teal mr-2 font-bold">
                    {index + 1}.
                  </span>
                  {item.title}
                </h3>
              </div>
              
              {/* Conteúdo sempre visível */}
              <div className="p-4">
                {/* Imagens do bloco */}
                {item.images && item.images.length > 0 && (
                  <div className="mb-4">
                    <div className="grid grid-cols-2 gap-2">
                      {item.images.slice(0, 4).map((image: string, imgIndex: number) => (
                        <div key={imgIndex} className="h-32 overflow-hidden rounded">
                          <img 
                            src={image} 
                            alt={`${item.title} - Imagem ${imgIndex + 1}`} 
                            className="w-full h-full object-cover"
                          />
                        </div>
                      ))}
                    </div>
                    {item.images.length > 4 && (
                      <p className="text-xs text-gray-500 mt-1">
                        +{item.images.length - 4} imagens adicionais
                      </p>
                    )}
                  </div>
                )}
                
                {/* Texto do bloco */}
                {item.description && (
                  <div 
                    className="text-sm text-gray-700 leading-relaxed prose prose-sm max-w-none text-left"
                    dangerouslySetInnerHTML={{ 
                      __html: item.description
                        .replace(/style=\"[^\"]*\"/g, '')
                        .replace(/style='[^']*'/g, '')
                    }}
                  />
                )}
              </div>
            </div>
          );
        } else if (item.type === 'tour') {
          return (
            <div key={item.id} className="mb-4 border border-gray-200 rounded-lg overflow-hidden shadow-sm print-avoid-break">
              {/* Título do tour */}
              <div className="px-4 py-3 bg-gray-50 border-b">
                <h3 className="text-lg font-bold text-voah-teal flex items-center">
                  <span className="text-voah-teal mr-2 font-bold">
                    {index + 1}.
                  </span>
                  {item.name}
                </h3>
              </div>
              
              {/* Conteúdo sempre visível */}
              <div className="p-4">
                {/* Imagens do tour */}
                {item.images && item.images.length > 0 && (
                  <div className="mb-4">
                    <div className="grid grid-cols-2 gap-2">
                      {item.images.slice(0, 4).map((image: string, imgIndex: number) => (
                        <div key={imgIndex} className="h-32 overflow-hidden rounded">
                          <img 
                            src={image} 
                            alt={`${item.name} - Imagem ${imgIndex + 1}`} 
                            className="w-full h-full object-cover"
                          />
                        </div>
                      ))}
                    </div>
                    {item.images.length > 4 && (
                      <p className="text-xs text-gray-500 mt-1">
                        +{item.images.length - 4} imagens adicionais
                      </p>
                    )}
                  </div>
                )}
                
                {/* Texto do tour */}
                {item.description && (
                  <div 
                    className="text-sm text-gray-700 leading-relaxed prose prose-sm max-w-none text-left"
                    dangerouslySetInnerHTML={{ 
                      __html: item.description
                        .replace(/style=\"[^\"]*\"/g, '')
                        .replace(/style='[^']*'/g, '')
                    }}
                  />
                )}
              </div>
            </div>
          );
        }
        
        return null;
      })}
    </div>
  );
} 